import React, { useState, useCallback, useEffect, useRef } from 'react';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useStyles } from './ActionsMenu.styles';
import { ActionMenuItem, ActionsMenuProps } from './ActionsMenu.types';
import IconButton from '@material-ui/core/IconButton';
import ActionsMenuItems from './ActionsMenuItems.component';
import { useTabNavigationDetails } from '../../Context/TabNavigation.context';
import TooltipOne from '../TooltipOne/TooltipOne.component';
import { useIsAnyDropdownOpen } from '../../Context/Layout.context';
import { fill, fillBlue } from '../../utils/contrastSvgAttributes';

const ActionsMenu: React.FC<ActionsMenuProps> = (props) => {
  const classes = useStyles();

  const { tabIndexForChildren, isDuringAction, activeRowIndex, forceStopAction } = useTabNavigationDetails();

  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<SVGSVGElement | HTMLElement | undefined>(undefined);
  const [menuItems, setMenuItems] = useState<ActionMenuItem[] | undefined>(props.menuItems);
  const iconRef = useRef<SVGSVGElement | null>(null);
  const isAnyDropdownOpen = useIsAnyDropdownOpen();

  const handleActionsMenuOpen = useCallback(
    (element: HTMLElement | SVGSVGElement): void => {
      if (props.menuItems instanceof Function) {
        setMenuItems(props.menuItems());
      }

      if (props.onOpen !== undefined) props.onOpen();
      setActionsMenuAnchor(element);
      isAnyDropdownOpen.current = true;
    },
    [props, isAnyDropdownOpen],
  );
  const handleCloseMenu = useCallback((): void => {
    setActionsMenuAnchor(undefined);
    isAnyDropdownOpen.current = false;
    props.onClose && props.onClose();
  }, [isAnyDropdownOpen, props]);

  useEffect(() => {
    if (!(props.menuItems instanceof Function)) {
      setMenuItems(props.menuItems);
    }
  }, [props.menuItems]);

  useEffect(() => {
    if (isDuringAction && activeRowIndex === props.rowIndex) {
      iconRef.current?.focus();
      forceStopAction?.();
    }
  }, [activeRowIndex, isDuringAction, props.rowIndex, forceStopAction]);

  return (
    <>
      {menuItems?.length && actionsMenuAnchor ? (
        <ActionsMenuItems
          menuItems={menuItems}
          anchorEl={actionsMenuAnchor}
          anchorOrigin={
            props.anchorOrigin ?? {
              vertical: 'bottom',
              horizontal: 'left',
            }
          }
          transformOrigin={
            props.transformOrigin ?? {
              vertical: 'top',
              horizontal: 'right',
            }
          }
          onCloseMenu={handleCloseMenu}
          onClickAnyItem={() => setActionsMenuAnchor(undefined)}
        />
      ) : null}
      <TooltipOne title="Więcej akcji" placement="top">
        <IconButton
          disableRipple
          style={props.iconStyle}
          aria-label="Akcje"
          size="small"
          onClick={(event: React.MouseEvent<HTMLElement>) => {
            handleActionsMenuOpen(event.target as HTMLElement);
          }}
          onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
            if (event.key === 'Enter') {
              handleActionsMenuOpen(event.target as HTMLElement);
            }
          }}
          tabIndex={props.isNotInTable ? 0 : -1}
          aria-expanded={actionsMenuAnchor !== undefined}
        >
          {props.icon || (
            <MoreVertIcon
              className={props.iconStyle ? classes.hoover : classes.actionIcon}
              aria-controls={actionsMenuAnchor ? 'action-menu' : undefined}
              aria-haspopup="true"
              aria-hidden="false"
              tabIndex={isDuringAction ? 0 : tabIndexForChildren}
              ref={iconRef}
              fill={props.isNotInTable ? fill : fillBlue}
            />
          )}
        </IconButton>
      </TooltipOne>
    </>
  );
};

export default ActionsMenu;
